import { RNG } from "rot-js";
import { logInDev } from "../../../debug";
import { Bounds, Point } from "../../../engine/space/bounds";
import { BaseDirections, Directions } from "../../../engine/space/directions";
import { FloorTypes } from "../Map";
import { BasicMap, BasicTile } from "../BasicMap";

export const setAreaZLevel = (map: BasicMap, bounds: Bounds, z: number) => {
  for (let y = bounds.y; y < bounds.h + bounds.y; y++) {
    for (let x = bounds.x; x < bounds.w + bounds.x; x++) {
      map.tileAt(x, y).z = z;
    }
  }
};

export const makeEmptyRoom = (map: BasicMap, bounds: Bounds) => {
  const point = { x: bounds.x, y: bounds.y };
  let tile;
  for (; point.y < bounds.h + bounds.y; point.y++) {
    point.x = bounds.x;
    for (; point.x < bounds.w + bounds.x; point.x++) {
      tile = map.tile(point);
      tile.type = FloorTypes.EMPTY;
    }
  }
};

const getHorzRandom = (rng: typeof RNG, a: Bounds, b: Bounds) => {
  const minX = Math.max(a.x, b.x);
  const maxX = Math.min(a.x + a.w - 1, b.x + b.w - 1);
  if (maxX - minX < 3) {
    return rng.getUniformInt(minX, maxX);
  } else {
    return rng.getUniformInt(minX + 1, maxX - 1);
  }
};

const getVertRandom = (rng: typeof RNG, a: Bounds, b: Bounds) => {
  const minY = Math.max(a.y, b.y);
  const maxY = Math.min(a.y + a.h - 1, b.y + b.h - 1);
  if (maxY - minY < 3) {
    return rng.getUniformInt(minY, maxY);
  } else {
    return rng.getUniformInt(minY + 1, maxY - 1);
  }
};

const eachVertShared = (
  map: BasicMap,
  left: Bounds,
  right: Bounds,
  cb: (a: BasicTile, b: BasicTile, dir: BaseDirections, pos: Point) => void
) => {
  const leftSide = left.x + left.w - 1;
  let y = Math.max(left.y, right.y);
  const maxY = Math.min(left.y + left.h, right.y + right.h);
  for (; y < maxY; y++) {
    cb(map.tileAt(leftSide, y), map.tileAt(right.x, y), Directions.Right, {
      x: leftSide,
      y,
    });
  }
};

const eachHorzShared = (
  map: BasicMap,
  top: Bounds,
  bot: Bounds,
  cb: (a: BasicTile, b: BasicTile, dir: BaseDirections, pos: Point) => void
) => {
  const topSide = top.y + top.h - 1;
  let x = Math.max(top.x, bot.x);
  const maxX = Math.min(top.x + top.w, bot.x + bot.w);
  for (; x < maxX; x++) {
    cb(map.tileAt(x, topSide), map.tileAt(x, bot.y), Directions.Down, {
      x,
      y: topSide,
    });
  }
};

const setHorzRandom = (
  map: BasicMap,
  rng: typeof RNG,
  left: Bounds,
  right: Bounds,
  cb: (tile: BasicTile, neighbor: BasicTile, dir: BaseDirections) => void
) => {
  const y = getVertRandom(rng, left, right);
  cb(
    map.tileAt(left.x + left.w - 1, y),
    map.tileAt(right.x, y),
    Directions.Right
  );
};

const setVertRandom = (
  map: BasicMap,
  rng: typeof RNG,
  top: Bounds,
  bot: Bounds,
  cb: (tile: BasicTile, neighbor: BasicTile, dir: BaseDirections) => void
) => {
  const x = getHorzRandom(rng, top, bot);
  cb(map.tileAt(x, top.y + top.h - 1), map.tileAt(x, bot.y), Directions.Down);
};

export const forEachShared = (
  map: BasicMap,
  a: Bounds,
  b: Bounds,
  cb: (
    tile: BasicTile,
    neighbor: BasicTile,
    dir: BaseDirections,
    pos: Point
  ) => void
) => {
  if (a.x === b.x + b.w) {
    eachVertShared(map, b, a, cb);
  } else if (a.x + a.w === b.x) {
    eachVertShared(map, a, b, cb);
  } else if (a.y === b.y + b.h) {
    eachHorzShared(map, b, a, cb);
  } else if (a.y + a.h === b.y) {
    eachHorzShared(map, a, b, cb);
  } else {
    logInDev(`No Shared between rooms: ${a} ad ${b}`);
  }
};

export const oneRandomShared = (
  map: BasicMap,
  rng: typeof RNG,
  a: Bounds,
  b: Bounds,
  cb: (tile: BasicTile, neighbor: BasicTile, dir: BaseDirections) => void
) => {
  if (a.x === b.x + b.w) {
    setVertRandom(map, rng, a, b, cb);
  } else if (a.x + a.w === b.x) {
    setVertRandom(map, rng, b, a, cb);
  } else if (a.y === b.y + b.h) {
    setHorzRandom(map, rng, a, b, cb);
  } else if (a.y + a.h === b.y) {
    setHorzRandom(map, rng, b, a, cb);
  } else {
    logInDev(`No Shared between rooms: ${a} ad ${b}`);
    return;
  }
};

export const getSharedSpan = (map: BasicMap, a: Bounds, b: Bounds) => {
  let pos, min, max, vertical;
  if (a.x === b.x + b.w) {
    min = Math.max(a.y, b.y);
    max = Math.min(a.y + a.h, b.y + b.h) - 1;
    vertical = true;
    pos = a.x;
  } else if (a.x + a.w === b.x) {
    min = Math.max(a.y, b.y);
    max = Math.min(a.y + a.h, b.y + b.h) - 1;
    vertical = true;
    pos = b.x;
  } else if (a.y === b.y + b.h) {
    min = Math.max(a.x, b.x);
    max = Math.min(a.x + b.w, b.x + b.w) - 1;
    vertical = false;
    pos = a.y;
  } else if (a.y + a.h === b.y) {
    min = Math.max(a.x, b.x);
    max = Math.min(a.x + b.w, b.x + b.w) - 1;
    vertical = false;
    pos = b.y;
  } else {
    logInDev(`No Shared between rooms: ${a} ad ${b}`);
    return { min: 0, max: 0, vertical: false, pos: 0 };
  }

  return { min, max, vertical, pos };
};
